import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { usePermissions } from '../../hooks/usePermissions';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { SystemModule } from '../../types/permissions';
import ModalPortal from '../../components/Common/ModalPortal';
import ImportModal from '../../components/Common/ImportModal';
import { abastecimentosImportConfig } from '../../config/importConfigs';

interface FuelingItem {
  id: number;
  veiculo: string;
  motorista: string;
  data_abastecimento: string;
  combustivel: string;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;
  hodometro: number;
  posto: string;
  local: string;
  observacoes?: string;
  status: string;
}

const Fueling: React.FC = () => {
  console.count('[FUELING COMPONENT] Renderizando componente');

  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [filter, setFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [fuelFilter, setFuelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExcelImportModal, setShowExcelImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importStartDate, setImportStartDate] = useState('');
  const [importEndDate, setImportEndDate] = useState('');
  const [importStatus, setImportStatus] = useState<any>(null);
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [, setPollingErrorCount] = useState(0);
  const [vinculandoOrfaos, setVinculandoOrfaos] = useState(false);

  // Ref para prevenir múltiplas execuções simultâneas (proteção contra React.StrictMode)
  const isImportingRef = useRef(false);

  // Hook de infinite scroll
  const {
    items: fuelings,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore,
    reset: resetFuelings
  } = useInfiniteScroll<FuelingItem>({
    fetchFunction: async (page, limit, filters) => {
      try {
        const response = await apiService.getFuelings({
          page,
          limit,
          veiculo: filters?.veiculo || undefined,
          combustivel: filters?.combustivel || undefined,
          status: filters?.status || undefined
        });

        if (response.success && response.data) {
          const abastecimentos = Array.isArray(response.data) ? response.data :
                                 (response.data as any).abastecimentos || [];

          // Abastecimento usa PT-BR (sucesso/dados/paginacao) que é convertido pelo apiService
          // A paginação está no nível root do response após conversão
          return {
            success: true,
            data: {
              items: abastecimentos,
              pagination: {
                total: (response as any).pagination?.total || abastecimentos.length
              }
            }
          };
        }

        return {
          success: true,
          data: { items: [], pagination: { total: 0 } }
        };
      } catch (error) {
        console.error('Erro ao carregar abastecimentos:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar abastecimentos'
        };
      }
    },
    itemsPerPage: 10,
    filters: { veiculo: vehicleFilter, combustivel: fuelFilter, status: statusFilter },
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  // Cleanup do pollingInterval quando o componente desmonta ou quando o modal é fechado
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Cleanup quando o modal é fechado
  useEffect(() => {
    if (!showImportModal && pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [showImportModal, pollingInterval]);

  const checkImportStatus = async (importacaoId: number) => {
    console.log(`[CHECK STATUS] Verificando status da importação ID ${importacaoId}...`);
    try {
      const response: any = await apiService.get('FUELING_IMPORT_BY_ID', { id: importacaoId });

      if (response.success && response.data) {
        const importacao = response.data;
        console.log(`[CHECK STATUS] Status recebido:`, {
          id: importacao.id,
          status: importacao.status,
          registros_importados: importacao.registros_importados,
          registros_atualizados: importacao.registros_atualizados,
          registros_com_erro: importacao.registros_com_erro,
          total_registros_api: importacao.total_registros_api
        });

        setImportStatus(importacao);
        setImportErrors(importacao.erros || []);
        setPollingErrorCount(0); // Resetar contador de erros em caso de sucesso

        // Se a importação terminou (sucesso ou erro), para o polling
        if (importacao.status === 'concluido' || importacao.status === 'concluido_com_erros' || importacao.status === 'erro') {
          console.log(`[CHECK STATUS] ✅ Importação finalizada com status: ${importacao.status}`);
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          setImportLoading(false);

          // Mostrar notificação de sucesso ou erro
          if (importacao.status === 'concluido') {
            // Verificar se não houve dados importados
            const totalImportados = (importacao.registros_importados || 0) + (importacao.registros_atualizados || 0);
            const veiculosNaoEncontrados = importacao.total_veiculos_nao_encontrados || 0;

            if (totalImportados === 0) {
              utils.showNotification(
                'Importação concluída! Nenhum abastecimento novo encontrado no período selecionado.',
                { type: 'info' }
              );
            } else if (veiculosNaoEncontrados > 0) {
              utils.showNotification(
                `Importação concluída! ${importacao.registros_importados} novos, ${importacao.registros_atualizados} atualizados. ⚠️ ${veiculosNaoEncontrados} veículo(s) não encontrado(s).`,
                { type: 'warning' }
              );
            } else {
              utils.showNotification(
                `Importação concluída! ${importacao.registros_importados} novos, ${importacao.registros_atualizados} atualizados.`,
                { type: 'success' }
              );
            }
          } else if (importacao.status === 'concluido_com_erros') {
            utils.showNotification(
              `Importação concluída com erros: ${importacao.registros_importados} novos, ${importacao.registros_atualizados} atualizados, ${importacao.registros_com_erro} erros.`,
              { type: 'warning' }
            );
          } else if (importacao.status === 'erro') {
            utils.showNotification(
              `Erro na importação: ${importacao.detalhes_erro || 'Erro desconhecido'}`,
              { type: 'error' }
            );
          }

          // Recarregar a lista de abastecimentos
          resetFuelings();
        }
      }
    } catch (error) {
      console.error('Erro ao verificar status da importação:', error);

      // Incrementar contador de erros
      setPollingErrorCount(prev => {
        const newErrorCount = prev + 1;

        // Se houver muitos erros consecutivos (5), parar o polling
        if (newErrorCount >= 5) {
          console.error('Muitos erros consecutivos ao verificar status, parando polling');
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          setImportLoading(false);
          utils.showNotification(
            'Não foi possível acompanhar o progresso da importação. Verifique o histórico de importações (botão "Histórico" no topo da página).',
            { type: 'error' }
          );
        }

        return newErrorCount;
      });
    }
  };

  // Calcular data máxima (hoje) para o input HTML
  const getMaxDate = () => {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  };

  const handleImportCtaSmart = async () => {
    const executionId = Date.now(); // ID único para esta execução
    console.log(`[CTA Smart Import #${executionId}] Iniciando importação manual...`);
    console.log(`[CTA Smart Import #${executionId}] Data início:`, importStartDate);
    console.log(`[CTA Smart Import #${executionId}] Data fim:`, importEndDate);
    console.log(`[CTA Smart Import #${executionId}] isImportingRef.current:`, isImportingRef.current);
    console.log(`[CTA Smart Import #${executionId}] importLoading state:`, importLoading);

    // Prevenir múltiplas execuções simultâneas usando useRef (proteção contra React.StrictMode)
    if (isImportingRef.current) {
      console.warn(`[CTA Smart Import #${executionId}] ❌ BLOQUEADO - Importação já em andamento (ref check), ignorando clique duplicado`);
      return;
    }

    // Verificação adicional com state
    if (importLoading) {
      console.warn(`[CTA Smart Import #${executionId}] ❌ BLOQUEADO - Importação já em andamento (state check), ignorando clique duplicado`);
      return;
    }

    // Marcar como importando IMEDIATAMENTE (ref é síncrono, não depende de re-render)
    console.log(`[CTA Smart Import #${executionId}] ✅ Marcando isImportingRef.current = true`);
    isImportingRef.current = true;

    // Setar loading para UI
    setImportLoading(true);
    setImportStatus(null);
    setImportErrors([]);
    setPollingErrorCount(0);

    if (!importStartDate || !importEndDate) {
      utils.showNotification('Por favor, preencha as datas de início e fim', { type: 'error' });
      isImportingRef.current = false;
      setImportLoading(false);
      return;
    }

    // Validar intervalo de datas (máximo 90 dias)
    const dateInicio = new Date(importStartDate);
    const dateFim = new Date(importEndDate);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    if (dateFim < dateInicio) {
      utils.showNotification('A data fim deve ser maior ou igual à data início', { type: 'error' });
      isImportingRef.current = false;
      setImportLoading(false);
      return;
    }

    // Validar se a data fim não é futura
    const dateFimNorm = new Date(dateFim);
    dateFimNorm.setHours(0, 0, 0, 0);

    if (dateFimNorm > hoje) {
      const diasFuturos = Math.ceil((dateFimNorm.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
      utils.showNotification(
        `Não é permitido importar dados futuros. A data fim está ${diasFuturos} dia(s) no futuro.`,
        { type: 'error' }
      );
      isImportingRef.current = false;
      setImportLoading(false);
      return;
    }

    const diffTime = Math.abs(dateFim.getTime() - dateInicio.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const MAX_DIAS_INTERVALO = 90;

    if (diffDays > MAX_DIAS_INTERVALO) {
      utils.showNotification(
        `O intervalo máximo permitido é de ${MAX_DIAS_INTERVALO} dias. Intervalo selecionado: ${diffDays} dias.`,
        { type: 'error' }
      );
      isImportingRef.current = false;
      setImportLoading(false);
      return;
    }

    try {
      // Formatar datas para o formato dd/mm/yyyy
      const formatDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
      };

      console.log(`[CTA Smart Import #${executionId}] 📤 Enviando requisição POST para API...`);
      const response: any = await apiService.post('FUELING_IMPORT_CTA_SMART', {
        data_inicio: formatDate(importStartDate),
        data_fim: formatDate(importEndDate),
        hora_inicio: '00:00',
        hora_fim: '23:59'
      });
      console.log(`[CTA Smart Import #${executionId}] 📥 Resposta recebida:`, response);

      if (response.success && response.data?.importacaoId) {
        const importacaoId = response.data.importacaoId;

        // Importação iniciada com sucesso, resetar ref (será resetado novamente quando finalizar)
        isImportingRef.current = false;

        utils.showNotification(
          'Importação iniciada! Acompanhe o progresso abaixo.',
          { type: 'info' }
        );

        // Iniciar polling para acompanhar o progresso
        const interval = setInterval(() => {
          checkImportStatus(importacaoId);
        }, 5000); // Verificar a cada 5 segundos (reduzido de 2s para diminuir carga)

        setPollingInterval(interval);

        // Timeout de 10 minutos - se não finalizar, para o polling
        setTimeout(() => {
          console.warn('[POLLING TIMEOUT] Importação não finalizou em 10 minutos, parando polling');
          if (interval) {
            clearInterval(interval);
            setPollingInterval(null);
            setImportLoading(false);
            utils.showNotification(
              'A importação está demorando mais que o esperado. Verifique o histórico de importações.',
              { type: 'warning' }
            );
          }
        }, 600000); // 10 minutos

        // Primeira verificação imediata
        checkImportStatus(importacaoId);
      } else {
        utils.showNotification(response.message || 'Erro ao iniciar importação', { type: 'error' });
        isImportingRef.current = false;
        setImportLoading(false);
      }
    } catch (error: any) {
      console.error('Erro ao importar:', error);
      const mensagem = error.message || 'Erro ao iniciar importação de abastecimentos da CTA Smart';
      utils.showNotification(mensagem, { type: 'error' });
      isImportingRef.current = false;
      setImportLoading(false);
    }
  };

  const handleVincularOrfaos = async () => {
    console.log('[Vincular Órfãos] Iniciando vinculação automática...');

    try {
      setVinculandoOrfaos(true);

      const response: any = await apiService.post('FUELING_VINCULAR_ORFAOS');

      if (response.success && response.data) {
        const { total_vinculados, total_processados, placas_encontradas } = response.data;

        if (total_vinculados > 0) {
          utils.showNotification(
            `${total_vinculados} abastecimento(s) vinculado(s) com sucesso! (${placas_encontradas} placa(s) encontrada(s))`,
            { type: 'success' }
          );

          // Recarregar os detalhes da importação para atualizar os veículos não encontrados
          if (importStatus && importStatus.id) {
            await checkImportStatus(importStatus.id);
          }

          // Recarregar a lista de abastecimentos
          resetFuelings();
        } else {
          utils.showNotification(
            `Nenhum abastecimento vinculado. ${total_processados} placa(s) ainda não estão cadastradas.`,
            { type: 'info' }
          );
        }
      } else {
        utils.showNotification(response.message || 'Erro ao vincular abastecimentos', { type: 'error' });
      }
    } catch (error: any) {
      console.error('Erro ao vincular órfãos:', error);
      utils.showNotification(error.message || 'Erro ao vincular abastecimentos órfãos', { type: 'error' });
    } finally {
      setVinculandoOrfaos(false);
    }
  };

  // Limpar polling ao desmontar o componente
  React.useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Concluído': 'status-success',
      'Pendente': 'status-warning',
      'Cancelado': 'status-danger'
    };
    return statusMap[status] || 'status-secondary';
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const filteredFuelings = fuelings.filter(fueling => {
    const matchesSearch = fueling.veiculo.toLowerCase().includes(filter.toLowerCase()) ||
                         fueling.motorista.toLowerCase().includes(filter.toLowerCase()) ||
                         fueling.posto.toLowerCase().includes(filter.toLowerCase()) ||
                         fueling.local.toLowerCase().includes(filter.toLowerCase());
    const matchesVehicle = !vehicleFilter || fueling.veiculo === vehicleFilter;
    const matchesFuel = !fuelFilter || fueling.combustivel === fuelFilter;
    const matchesStatus = !statusFilter || fueling.status === statusFilter;
    return matchesSearch && matchesVehicle && matchesFuel && matchesStatus;
  });

  const viewFueling = (id: number) => {
    navigate(`/abastecimentos/visualizar/${id}`);
  };

  const editFueling = (id: number) => {
    navigate(`/abastecimentos/editar/${id}`);
  };

  const deleteFueling = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir este abastecimento?')) {
      try {
        const response = await apiService.deleteFueling(id);

        if (response.success) {
          utils.showNotification('Abastecimento excluído com sucesso!', { type: 'success' });
          // Recarrega a lista após deletar
          resetFuelings();
        } else {
          throw new Error(response.message || 'Erro ao excluir abastecimento');
        }
      } catch (error: any) {
        console.error('Erro ao excluir abastecimento:', error);
        utils.showNotification(error.message || 'Erro ao excluir abastecimento', { type: 'error' });
      }
    }
  };

  const getVehicles = () => {
    const vehicles = Array.from(new Set(fuelings.map(f => f.veiculo)));
    return vehicles;
  };

  const getFuels = () => {
    const fuels = Array.from(new Set(fuelings.map(f => f.combustivel)));
    return fuels;
  };

  const getStatuses = () => {
    const statuses = Array.from(new Set(fuelings.map(f => f.status)));
    return statuses;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Abastecimentos</h1>
          <button className="btn btn-primary" disabled>
            <i className="fas fa-plus"></i>
            Novo Abastecimento
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando abastecimentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Abastecimentos</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {canCreate(SystemModule.ABASTECIMENTOS) && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowExcelImportModal(true)}
              title="Importar via planilha Excel"
            >
              <i className="fas fa-file-excel"></i>
              Importar Planilha
            </button>
          )}
          {canCreate(SystemModule.ABASTECIMENTOS) && (
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate('/abastecimentos/importacoes')}
              title="Ver histórico de importações CTA Smart"
            >
              <i className="fas fa-history"></i>
              Histórico
            </button>
          )}
          {canCreate(SystemModule.ABASTECIMENTOS) && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-download"></i>
              Importar CTA Smart
            </button>
          )}
          {canCreate(SystemModule.ABASTECIMENTOS) && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/abastecimentos/novo')}
            >
              <i className="fas fa-plus"></i>
              Novo Abastecimento
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Registro de Abastecimentos</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Veículo, motorista, posto ou local...'
                },
                {
                  type: 'select',
                  label: 'Veículo',
                  value: vehicleFilter,
                  onChange: setVehicleFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getVehicles().map(vehicle => ({ value: vehicle, label: vehicle }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Combustível',
                  value: fuelFilter,
                  onChange: setFuelFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getFuels().map(fuel => ({ value: fuel, label: fuel }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getStatuses().map(status => ({ value: status, label: status }))
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setVehicleFilter('');
                setFuelFilter('');
                setStatusFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Abastecimentos"
            />
          </div>
          
          <div className="card-body">
            {/* Contador de resultados */}
            {filteredFuelings.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="abastecimentos"
              />
            )}

            {filteredFuelings.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-gas-pump empty-state-icon"></i>
                <h3>Nenhum abastecimento encontrado</h3>
                <p>{filter || vehicleFilter || fuelFilter || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Registre o primeiro abastecimento do sistema.'}</p>
                {canCreate(SystemModule.ABASTECIMENTOS) && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/abastecimentos/novo')}
                  >
                    Registrar Primeiro Abastecimento
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Data/Hora</th>
                      <th>Veículo</th>
                      <th>Motorista</th>
                      <th>Combustível</th>
                      <th>Quantidade</th>
                      <th>Valor Unitário</th>
                      <th>Valor Total</th>
                      <th>Hodômetro</th>
                      <th>Posto</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFuelings.map(fueling => (
                      <tr key={fueling.id}>
                        <td>
                          <div style={{ fontSize: '0.8rem' }}>
                            {formatDate(fueling.data_abastecimento)}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#7f8c8d' }}>
                            {formatDateTime(fueling.data_abastecimento).split(', ')[1]}
                          </div>
                        </td>
                        <td>
                          <strong>{fueling.veiculo}</strong>
                        </td>
                        <td>{fueling.motorista}</td>
                        <td>
                          <span className="status-badge status-info">
                            {fueling.combustivel}
                          </span>
                        </td>
                        <td>
                          {formatNumber(fueling.quantidade)} L
                        </td>
                        <td>
                          {formatCurrency(fueling.valor_unitario)}
                        </td>
                        <td>
                          <strong>{formatCurrency(fueling.valor_total)}</strong>
                        </td>
                        <td>
                          {fueling.hodometro.toLocaleString('pt-BR')} km
                        </td>
                        <td>
                          <div style={{ fontSize: '0.8rem' }}>
                            {fueling.posto}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#7f8c8d' }}>
                            {fueling.local}
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusClass(fueling.status)}`}>
                            {fueling.status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-view"
                              onClick={() => viewFueling(fueling.id)}
                              title="Visualizar"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            {canUpdate(SystemModule.ABASTECIMENTOS) && (
                              <button
                                className="btn-edit"
                                onClick={() => editFueling(fueling.id)}
                                title="Editar"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                            {canDelete(SystemModule.ABASTECIMENTOS) && (
                              <button
                                className="btn-delete"
                                onClick={() => deleteFueling(fueling.id)}
                                title="Excluir"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas dos abastecimentos */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-gas-pump"></i>
            </div>
            <div className="stat-value">
              {fuelings.length}
            </div>
            <div className="stat-label">Total de Abastecimentos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-oil-can"></i>
            </div>
            <div className="stat-value">
              {formatNumber(fuelings.reduce((total, f) => total + f.quantidade, 0))}
            </div>
            <div className="stat-label">Litros Abastecidos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div className="stat-value">
              {formatCurrency(fuelings.reduce((total, f) => total + f.valor_total, 0))}
            </div>
            <div className="stat-label">Valor Total</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-car"></i>
            </div>
            <div className="stat-value">
              {getVehicles().length}
            </div>
            <div className="stat-label">Veículos Abastecidos</div>
          </div>
        </div>

        {/* Média de consumo por veículo */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Média de Consumo por Veículo</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              {getVehicles().map(vehicle => {
                const vehicleFuelings = fuelings.filter(f => f.veiculo === vehicle);
                const totalLiters = vehicleFuelings.reduce((total, f) => total + f.quantidade, 0);
                const totalValue = vehicleFuelings.reduce((total, f) => total + f.valor_total, 0);
                const avgPrice = totalLiters > 0 ? totalValue / totalLiters : 0;
                
                return (
                  <div key={vehicle} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{vehicle}</span>
                      <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{vehicleFuelings.length}</span>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                      <div>Total abastecido: <strong>{formatNumber(totalLiters)} L</strong></div>
                      <div>Valor total: <strong>{formatCurrency(totalValue)}</strong></div>
                      <div>Preço médio: <strong>{formatCurrency(avgPrice)}/L</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Distribuição por combustível */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Distribuição por Combustível</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              {getFuels().map(fuel => {
                const fuelFuelings = fuelings.filter(f => f.combustivel === fuel);
                const totalLiters = fuelFuelings.reduce((total, f) => total + f.quantidade, 0);
                const totalValue = fuelFuelings.reduce((total, f) => total + f.valor_total, 0);
                const percentage = Math.round((fuelFuelings.length / fuelings.length) * 100);
                
                return (
                  <div key={fuel} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{fuel}</span>
                      <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{fuelFuelings.length}</span>
                    </div>
                    <div style={{ background: '#e9ecef', borderRadius: '4px', height: '8px' }}>
                      <div 
                        style={{ 
                          background: 'linear-gradient(135deg, #04C64F, #03a542)', 
                          height: '100%', 
                          borderRadius: '4px', 
                          width: `${percentage}%` 
                        }}
                      ></div>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#7f8c8d' }}>
                      <div>Total: <strong>{formatNumber(totalLiters)} L</strong></div>
                      <div>Valor: <strong>{formatCurrency(totalValue)}</strong></div>
                      <div>Média: <strong>{formatCurrency(totalValue / totalLiters)}/L</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Importação CTA Smart */}
      <ModalPortal isOpen={showImportModal}>
        <div className="modal-overlay" onClick={() => !importLoading && setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Importar Abastecimentos da CTA Smart</h2>
              <button
                className="modal-close"
                onClick={() => setShowImportModal(false)}
                disabled={importLoading}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-section">
                {!importLoading && (
                  <>
                    <p style={{ marginBottom: '20px', color: '#7f8c8d' }}>
                      Selecione o período para importar os abastecimentos da API CTA Smart.
                      Os dados serão sincronizados automaticamente com o sistema.
                    </p>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="import-start-date">
                          Data Início <span className="required">*</span>
                        </label>
                        <input
                          type="date"
                          id="import-start-date"
                          className="form-control"
                          value={importStartDate}
                          onChange={(e) => setImportStartDate(e.target.value)}
                          disabled={importLoading}
                          max={getMaxDate()}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="import-end-date">
                          Data Fim <span className="required">*</span>
                        </label>
                        <input
                          type="date"
                          id="import-end-date"
                          className="form-control"
                          value={importEndDate}
                          onChange={(e) => setImportEndDate(e.target.value)}
                          disabled={importLoading}
                          max={getMaxDate()}
                          required
                        />
                      </div>
                    </div>

                    <div className="info-box" style={{ marginTop: '15px', padding: '15px', background: '#e8f5e9', borderRadius: '4px', border: '1px solid #81c784' }}>
                      <i className="fas fa-info-circle" style={{ color: '#4caf50', marginRight: '8px' }}></i>
                      <strong>Informações importantes:</strong>
                      <ul style={{ marginTop: '10px', paddingLeft: '20px', marginBottom: 0 }}>
                        <li>A importação pode levar alguns minutos dependendo do volume de dados</li>
                        <li>Abastecimentos duplicados serão atualizados automaticamente</li>
                        <li>Veículos serão vinculados automaticamente pela placa</li>
                        <li>Período máximo permitido: 90 dias (recomendado: 30 dias)</li>
                      </ul>
                    </div>
                  </>
                )}

                {/* Progresso da Importação */}
                {importLoading && importStatus && (
                  <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                      <div className="loading-spinner-large"></div>
                      <h3 style={{ marginTop: '15px', color: '#04364F' }}>
                        Importação em andamento...
                      </h3>
                      <p style={{ color: '#7f8c8d', marginTop: '10px' }}>
                        Status: <strong>{importStatus.status === 'em_andamento' ? 'Processando' : importStatus.status}</strong>
                      </p>
                      {importStatus.detalhes_erro && importStatus.status === 'em_andamento' && (
                        <p style={{
                          color: importStatus.detalhes_erro.includes('Limite de') ? '#d32f2f' : '#ff9800',
                          marginTop: '5px',
                          fontSize: '14px',
                          fontWeight: importStatus.detalhes_erro.includes('Limite de') ? 'bold' : 'normal'
                        }}>
                          <i className={importStatus.detalhes_erro.includes('Limite de') ? 'fas fa-exclamation-triangle' : 'fas fa-hourglass-half'}></i> {importStatus.detalhes_erro}
                        </p>
                      )}
                    </div>

                    {/* Estatísticas */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginTop: '20px' }}>
                      <div style={{ padding: '15px', background: '#e3f2fd', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
                          {importStatus.total_registros_api || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#555', marginTop: '5px' }}>
                          Total API
                        </div>
                      </div>

                      <div style={{ padding: '15px', background: '#e8f5e9', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#388e3c' }}>
                          {importStatus.registros_importados || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#555', marginTop: '5px' }}>
                          Novos
                        </div>
                      </div>

                      <div style={{ padding: '15px', background: '#fff3e0', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>
                          {importStatus.registros_atualizados || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#555', marginTop: '5px' }}>
                          Atualizados
                        </div>
                      </div>

                      <div style={{ padding: '15px', background: '#ffebee', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d32f2f' }}>
                          {importStatus.registros_com_erro || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#555', marginTop: '5px' }}>
                          Erros
                        </div>
                      </div>
                    </div>

                    {/* Lista de Erros */}
                    {importErrors.length > 0 && (
                      <div style={{ marginTop: '20px' }}>
                        <h4 style={{ marginBottom: '10px', color: '#d32f2f' }}>
                          <i className="fas fa-exclamation-triangle"></i> Erros Encontrados
                          {importStatus.tem_mais_erros && (
                            <span style={{ fontSize: '12px', color: '#ff9800', marginLeft: '10px' }}>
                              (Exibindo primeiros 100 de {importStatus.total_erros})
                            </span>
                          )}
                        </h4>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                          {importErrors.map((erro, index) => (
                            <div key={index} style={{ padding: '8px', background: '#fff', marginBottom: '8px', borderRadius: '4px', borderLeft: '3px solid #d32f2f' }}>
                              <div style={{ fontSize: '12px', color: '#d32f2f', fontWeight: 'bold' }}>
                                CTA Smart ID: {erro.cta_smart_id} - {erro.tipo_erro}
                              </div>
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                {erro.mensagem_erro}
                              </div>
                            </div>
                          ))}
                        </div>
                        {importStatus.tem_mais_erros && (
                          <div style={{ marginTop: '10px', padding: '10px', background: '#fff3e0', borderRadius: '4px', fontSize: '12px', color: '#f57c00' }}>
                            <i className="fas fa-info-circle"></i> Há mais {importStatus.total_erros - 100} erros não exibidos.{' '}
                            <button
                              className="btn btn-link"
                              style={{ padding: '0', fontSize: '12px', textDecoration: 'underline' }}
                              onClick={() => navigate('/abastecimentos/importacoes')}
                            >
                              Clique aqui para ver o histórico completo
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Resultado Final */}
                {!importLoading && importStatus && (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    {importStatus.status === 'concluido' && (
                      <div>
                        <i className="fas fa-check-circle" style={{ fontSize: '48px', color: '#4caf50' }}></i>
                        <h3 style={{ marginTop: '15px', color: '#4caf50' }}>Importação Concluída!</h3>
                      </div>
                    )}
                    {importStatus.status === 'concluido_com_erros' && (
                      <div>
                        <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#ff9800' }}></i>
                        <h3 style={{ marginTop: '15px', color: '#ff9800' }}>
                          {importStatus.detalhes_erro && importStatus.detalhes_erro.includes('IMPORTAÇÃO PARCIAL')
                            ? 'Importação Parcial Concluída'
                            : 'Importação Concluída com Erros'}
                        </h3>
                      </div>
                    )}
                    {importStatus.status === 'erro' && (
                      <div>
                        <i className="fas fa-times-circle" style={{ fontSize: '48px', color: '#f44336' }}></i>
                        <h3 style={{ marginTop: '15px', color: '#f44336' }}>Erro na Importação</h3>
                      </div>
                    )}

                    {/* Aviso de limite de registros */}
                    {importStatus.detalhes_erro && importStatus.detalhes_erro.includes('Limite de') && (
                      <div style={{ marginTop: '20px', padding: '15px', background: '#fff3e0', borderRadius: '8px', border: '2px solid #ff9800' }}>
                        <i className="fas fa-exclamation-triangle" style={{ color: '#f57c00', marginRight: '8px' }}></i>
                        <strong style={{ color: '#f57c00' }}>Atenção:</strong>
                        <div style={{ marginTop: '8px', color: '#666', fontSize: '14px', textAlign: 'left' }}>
                          {importStatus.detalhes_erro}
                        </div>
                      </div>
                    )}

                    {/* Estatísticas Finais */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginTop: '20px' }}>
                      <div style={{ padding: '15px', background: '#e8f5e9', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#388e3c' }}>
                          {importStatus.registros_importados || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#555', marginTop: '5px' }}>
                          Novos
                        </div>
                      </div>

                      <div style={{ padding: '15px', background: '#fff3e0', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>
                          {importStatus.registros_atualizados || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#555', marginTop: '5px' }}>
                          Atualizados
                        </div>
                      </div>

                      <div style={{ padding: '15px', background: '#ffebee', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d32f2f' }}>
                          {importStatus.registros_com_erro || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#555', marginTop: '5px' }}>
                          Erros
                        </div>
                      </div>
                    </div>

                    {/* Lista de Erros */}
                    {importErrors.length > 0 && (
                      <div style={{ marginTop: '20px' }}>
                        <h4 style={{ marginBottom: '10px', color: '#d32f2f', textAlign: 'left' }}>
                          <i className="fas fa-exclamation-triangle"></i> Detalhes dos Erros
                          {importStatus.tem_mais_erros && (
                            <span style={{ fontSize: '12px', color: '#ff9800', marginLeft: '10px' }}>
                              (Exibindo primeiros 100 de {importStatus.total_erros})
                            </span>
                          )}
                        </h4>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                          {importErrors.map((erro, index) => (
                            <div key={index} style={{ padding: '8px', background: '#fff', marginBottom: '8px', borderRadius: '4px', borderLeft: '3px solid #d32f2f', textAlign: 'left' }}>
                              <div style={{ fontSize: '12px', color: '#d32f2f', fontWeight: 'bold' }}>
                                CTA Smart ID: {erro.cta_smart_id} - {erro.tipo_erro}
                              </div>
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                {erro.mensagem_erro}
                              </div>
                            </div>
                          ))}
                        </div>
                        {importStatus.tem_mais_erros && (
                          <div style={{ marginTop: '10px', padding: '10px', background: '#fff3e0', borderRadius: '4px', fontSize: '12px', color: '#f57c00', textAlign: 'left' }}>
                            <i className="fas fa-info-circle"></i> Há mais {importStatus.total_erros - 100} erros não exibidos.{' '}
                            <button
                              className="btn btn-link"
                              style={{ padding: '0', fontSize: '12px', textDecoration: 'underline' }}
                              onClick={() => navigate('/abastecimentos/importacoes')}
                            >
                              Clique aqui para ver o histórico completo
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Veículos Não Encontrados */}
                    {importStatus.veiculos_nao_encontrados && importStatus.veiculos_nao_encontrados.length > 0 && (
                      <div style={{ marginTop: '20px' }}>
                        <h4 style={{ marginBottom: '10px', color: '#ff9800', textAlign: 'left' }}>
                          <i className="fas fa-car"></i> Veículos Não Encontrados
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px', fontWeight: 'normal' }}>
                            ({importStatus.total_abastecimentos_sem_veiculo} abastecimento(s) sem vínculo)
                          </span>
                        </h4>
                        <div style={{ marginBottom: '15px', padding: '12px', background: '#fff3e0', borderRadius: '4px', border: '1px solid #ff9800' }}>
                          <p style={{ fontSize: '13px', color: '#666', margin: 0, textAlign: 'left' }}>
                            <i className="fas fa-info-circle" style={{ color: '#ff9800', marginRight: '6px' }}></i>
                            Os abastecimentos abaixo foram importados mas não puderam ser vinculados automaticamente porque os veículos não estão cadastrados no sistema.
                          </p>
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                          {importStatus.veiculos_nao_encontrados.map((veiculo: any, index: number) => (
                            <div key={index} style={{ padding: '10px', background: '#fff', marginBottom: '8px', borderRadius: '4px', borderLeft: '3px solid #ff9800', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontSize: '14px', color: '#333', fontWeight: 'bold' }}>
                                  <i className="fas fa-car" style={{ color: '#ff9800', marginRight: '8px' }}></i>
                                  Placa: {veiculo.placa}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                  {veiculo.total} abastecimento(s) não vinculado(s)
                                </div>
                              </div>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => navigate('/veiculos/novo', { state: { placa: veiculo.placa } })}
                                style={{ fontSize: '11px', padding: '6px 12px' }}
                              >
                                <i className="fas fa-plus"></i> Cadastrar
                              </button>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginTop: '15px', padding: '12px', background: '#e3f2fd', borderRadius: '4px', border: '1px solid #2196f3', textAlign: 'left' }}>
                          <p style={{ fontSize: '13px', color: '#1976d2', margin: 0 }}>
                            <i className="fas fa-lightbulb" style={{ marginRight: '6px' }}></i>
                            <strong>Dica:</strong> Após cadastrar os veículos, use o botão "Vincular Abastecimentos" abaixo para vincular automaticamente os abastecimentos órfãos.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              {!importStatus && (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowImportModal(false)}
                    disabled={importLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.count('[BUTTON CLICK] Botão Importar clicado');
                      handleImportCtaSmart();
                    }}
                    disabled={importLoading || !importStartDate || !importEndDate}
                  >
                    <i className="fas fa-download"></i>
                    Importar
                  </button>
                </>
              )}

              {importStatus && importLoading && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowImportModal(false);
                    // Continuar polling em background
                  }}
                >
                  Fechar (Importação continua em background)
                </button>
              )}

              {importStatus && !importLoading && (
                <>
                  {importStatus.veiculos_nao_encontrados && importStatus.veiculos_nao_encontrados.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleVincularOrfaos}
                      disabled={vinculandoOrfaos}
                      style={{ marginRight: '10px' }}
                    >
                      {vinculandoOrfaos ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Vinculando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-link"></i> Vincular Abastecimentos
                        </>
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowImportModal(false);
                      setImportStatus(null);
                      setImportErrors([]);
                      setImportStartDate('');
                      setImportEndDate('');
                    }}
                    disabled={vinculandoOrfaos}
                  >
                    Fechar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </ModalPortal>

      <ImportModal
        isOpen={showExcelImportModal}
        onClose={() => setShowExcelImportModal(false)}
        onImportComplete={() => window.location.reload()}
        config={abastecimentosImportConfig}
      />
    </div>
  );
};

export default Fueling;
